import { Injectable, effect, signal } from '@angular/core';
import { Subject } from 'rxjs';

export type MenuMode = 'static' | 'overlay' | 'horizontal' | 'slim' | 'slim-plus' | 'reveal' | 'drawer';

export type ColorScheme = 'light' | 'dark';

export interface AppConfig {
  inputStyle: string;
  colorScheme: ColorScheme;
  componentTheme: string;
  menuMode: MenuMode;
  scale: number;
  menuTheme: string;
  topbarTheme: string;
  menuProfilePosition: string;
}

interface LayoutState {
  staticMenuMobileActive: boolean;
  overlayMenuActive: boolean;
  staticMenuDesktopInactive: boolean;
  configSidebarVisible: boolean;
  menuHoverActive: boolean;
  rightMenuActive: boolean;
  topbarMenuActive: boolean;
  menuProfileActive: boolean;
  sidebarActive: boolean;
  anchored: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class LayoutService {
  public _config: AppConfig = {
    inputStyle: 'outlined',
    menuMode: 'slim-plus',
    colorScheme: 'light',
    componentTheme: 'pflegix',
    scale: 14,
    menuTheme: 'light',
    topbarTheme: 'white',
    menuProfilePosition: 'end',
  };

  public state: LayoutState = {
    staticMenuDesktopInactive: false,
    overlayMenuActive: false,
    configSidebarVisible: false,
    staticMenuMobileActive: false,
    menuHoverActive: false,
    rightMenuActive: false,
    topbarMenuActive: false,
    menuProfileActive: false,
    sidebarActive: false,
    anchored: false,
  };

  public config = signal<AppConfig>(this._config);

  private configUpdate = new Subject<AppConfig>();

  private overlayOpen = new Subject<unknown>();

  private topbarMenuOpen = new Subject<unknown>();

  private menuProfileOpen = new Subject<unknown>();

  public configUpdate$ = this.configUpdate.asObservable();

  public overlayOpen$ = this.overlayOpen.asObservable();

  public topbarMenuOpen$ = this.topbarMenuOpen.asObservable();

  public menuProfileOpen$ = this.menuProfileOpen.asObservable();

  constructor() {
    effect(() => {
      const config = this.config();

      if (this.updateStyle(config)) {
        this.changeTheme();
      }

      this.changeScale(config.scale);
      this.onConfigUpdate();
    });
  }

  public updateStyle(config: AppConfig): boolean {
    return config.componentTheme !== this._config.componentTheme || config.colorScheme !== this._config.colorScheme;
  }

  public changeTheme(): void {
    const { colorScheme, componentTheme } = this.config();
    const themeLink = <HTMLLinkElement>document.getElementById('theme-link');
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const themeLinkHref = themeLink.getAttribute('href')!;
    const newHref = themeLinkHref
      .split('/')
      .map((el) => {
        let value = el;

        if (value === this._config.componentTheme) {
          value = componentTheme;
        } else if (value === `theme-${this._config.colorScheme}`) {
          value = `theme-${colorScheme}`;
        }

        return value;
      })
      .join('/');

    this.replaceThemeLink(newHref);
  }

  public replaceThemeLink(href: string): void {
    const id = 'theme-link';
    const themeLink = <HTMLLinkElement>document.getElementById(id);
    const cloneLinkElement = <HTMLLinkElement>themeLink.cloneNode(true);

    cloneLinkElement.setAttribute('href', href);
    cloneLinkElement.setAttribute('id', `${id}-clone`);

    themeLink.parentNode?.insertBefore(cloneLinkElement, themeLink.nextSibling);
    cloneLinkElement.addEventListener('load', () => {
      themeLink.remove();
      cloneLinkElement.setAttribute('id', id);
    });
  }

  public changeScale(value: number): void {
    document.documentElement.style.fontSize = `${value}px`;
  }

  public onMenuToggle(): void {
    if (this.isOverlay()) {
      this.state.overlayMenuActive = !this.state.overlayMenuActive;

      if (this.state.overlayMenuActive) {
        this.overlayOpen.next(null);
      }
    }

    if (this.isDesktop()) {
      this.state.staticMenuDesktopInactive = !this.state.staticMenuDesktopInactive;
    } else {
      this.state.staticMenuMobileActive = !this.state.staticMenuMobileActive;

      if (this.state.staticMenuMobileActive) {
        this.overlayOpen.next(null);
      }
    }
  }

  public onTopbarMenuToggle(): void {
    this.state.topbarMenuActive = !this.state.topbarMenuActive;
    if (this.state.topbarMenuActive) {
      this.topbarMenuOpen.next(null);
    }
  }

  public onOverlaySubmenuOpen(): void {
    this.overlayOpen.next(null);
  }

  public showConfigSidebar(): void {
    this.state.configSidebarVisible = true;
  }

  public isOverlay(): boolean {
    return this.config().menuMode === 'overlay';
  }

  public isDesktop(): boolean {
    return window.innerWidth > 991;
  }

  public isSlim(): boolean {
    return this.config().menuMode === 'slim';
  }

  public isSlimPlus(): boolean {
    return this.config().menuMode === 'slim-plus';
  }

  public isHorizontal(): boolean {
    return this.config().menuMode === 'horizontal';
  }

  public isMobile(): boolean {
    return !this.isDesktop();
  }

  public onConfigUpdate(): void {
    this._config = { ...this.config() };
    this.configUpdate.next(this.config());
  }

  public isRightMenuActive(): boolean {
    return this.state.rightMenuActive;
  }

  public openRightSidebar(): void {
    this.state.rightMenuActive = true;
  }

  public onMenuProfileToggle(): void {
    this.state.menuProfileActive = !this.state.menuProfileActive;
    if (this.state.menuProfileActive && this.isHorizontal() && this.isDesktop()) {
      this.menuProfileOpen.next(null);
    }
  }
}
